/**
 * MapView Component
 * Handles map instance, marker, POI clicks, and drag interactions
 */

import React, { useEffect, useRef } from 'react';
import { MapPin } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import type { PlaceData } from '@/types/location';
import type { PickerMode } from './LocationPickerState';

interface MapViewProps {
  mapRef: React.RefObject<HTMLDivElement>;
  defaultCenter?: { lat: number; lng: number }; // Initial center only (uncontrolled)
  selectedPlace: PlaceData | null;
  mode: PickerMode;
  onPOIClick: (placeId: string, lat?: number, lng?: number) => Promise<void>;
  onIdle?: (center: { lat: number; lng: number }) => void; // Called when map becomes idle after user interaction
  onLocateMe: () => void;
  language: 'en' | 'lv';
  disabled?: boolean;
  onMapReady?: (map: any, panToProgrammatic?: (center: { lat: number; lng: number }) => void) => void; // Callback to expose map instance and programmatic pan method
}

export const MapView: React.FC<MapViewProps> = ({
  mapRef,
  defaultCenter = { lat: 56.9496, lng: 24.1052 }, // Default to Riga
  selectedPlace,
  mode,
  onPOIClick,
  onIdle,
  onLocateMe,
  language,
  disabled = false,
  onMapReady,
}) => {
  const { t } = useI18n();
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const isProgrammaticMoveRef = useRef(false);
  const idleTimerRef = useRef<number | undefined>();
  const retryTimerRef = useRef<number | undefined>();
  
  // Store initial center - never update from props after mount (uncontrolled map)
  const initialCenterRef = useRef(defaultCenter);
  
  // State to trigger re-initialization when Google Maps becomes available
  const [mapsReady, setMapsReady] = React.useState(false);

  // Stable map options (uncontrolled map - only use initial center)
  const mapOptions = React.useMemo<google.maps.MapOptions>(() => ({
    center: initialCenterRef.current,
    zoom: 14,
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    clickableIcons: true,
    gestureHandling: 'greedy',
    tilt: 0, // Disable 3D tilt
    heading: 0, // Disable rotation
  }), []); // Empty deps - options never change after mount

  // Initialize map
  useEffect(() => {
    // Check if already initialized
    if (mapInstanceRef.current) {
      return; // Map already initialized
    }

    // Check prerequisites
    if (!mapRef.current) {
      console.log('[MapView] Waiting for mapRef to be attached...');
      return;
    }

    const googleMaps = window.google?.maps;
    if (!googleMaps?.Map) {
      console.log('[MapView] Waiting for Google Maps API to load...');
      // Poll for Google Maps to become available
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = window.setTimeout(() => {
        if (window.google?.maps?.Map) {
          console.log('[MapView] Google Maps API is now available, triggering re-initialization...');
          setMapsReady(true); // Trigger re-run of effect
        } else {
          // Keep polling
          setMapsReady(prev => !prev); // Toggle to trigger re-run
        }
      }, 500);
      return () => {
        window.clearTimeout(retryTimerRef.current);
      };
    }

    console.log('[MapView] Initializing map...');
    const map = new googleMaps.Map(mapRef.current, mapOptions);
    mapInstanceRef.current = map;
    
    // Hide 3D/tilt control after map is initialized (Google Maps renders it dynamically)
    const hideCameraControl = () => {
      // Try multiple selectors as Google Maps may use different class names
      const selectors = [
        'gmp-internal-camera-control.gmnoprint',
        '.gmp-internal-camera-control.gmnoprint',
        'gmp-internal-camera-control',
        '.gmp-internal-camera-control'
      ];
      
      selectors.forEach(selector => {
        const cameraControl = document.querySelector(selector);
        if (cameraControl) {
          const element = cameraControl as HTMLElement;
          element.style.display = 'none';
          element.style.visibility = 'hidden';
          element.style.opacity = '0';
          element.style.pointerEvents = 'none';
          console.log('[MapView] ✅ 3D/Tilt control hidden using selector:', selector);
        }
      });
    };
    
    // Try to hide immediately and also after delays (Google Maps renders controls asynchronously)
    hideCameraControl();
    setTimeout(hideCameraControl, 500);
    setTimeout(hideCameraControl, 1000);
    setTimeout(hideCameraControl, 2000);
    
    // Also listen for when controls are added to DOM
    let observer: MutationObserver | null = null;
    if (mapRef.current) {
      observer = new MutationObserver(() => {
        hideCameraControl();
      });
      observer.observe(mapRef.current, { childList: true, subtree: true });
    }
    
    // Programmatic pan method (sets flag to prevent idle handler from firing)
    const panToProgrammatic = (center: { lat: number; lng: number }) => {
      isProgrammaticMoveRef.current = true;
      // Preserve zoom level when panning programmatically - use panTo instead of setCenter
      const currentZoom = map.getZoom();
      map.panTo(center);
      // Explicitly restore zoom in case panTo changed it
      if (currentZoom !== null && currentZoom !== undefined) {
        requestAnimationFrame(() => {
          const newZoom = map.getZoom();
          if (Math.abs(newZoom - currentZoom) > 0.1 && map) {
            map.setZoom(currentZoom);
          }
        });
      }
      // Update marker immediately
      if (markerRef.current) {
        markerRef.current.setPosition(center);
      }
    };
    
    // Expose map instance and programmatic pan method to parent
    if (onMapReady) {
      onMapReady(map, panToProgrammatic);
      console.log('[MapView] Map instance and panToProgrammatic method exposed to parent');
    }

    // Create initial marker
    const marker = new googleMaps.Marker({
      position: initialCenterRef.current,
      map,
      icon: {
        url: '/icons/AddServiceEntry/pin.png',
        scaledSize: new googleMaps.Size(32, 32),
        anchor: new googleMaps.Point(16, 32),
        optimized: true,
      },
      draggable: false,
    });
    markerRef.current = marker;

    // Handle POI clicks
    map.addListener('click', (e: google.maps.MapMouseEvent & { placeId?: string }) => {
      // Check if click is on autocomplete dropdown
      const pacContainer = document.querySelector('.pac-container');
      if (pacContainer && e.domEvent) {
        const target = e.domEvent.target as HTMLElement;
        if (pacContainer.contains(target)) {
          console.log('[MapView] Click ignored - clicked on autocomplete dropdown');
          return;
        }
      }

      console.log('[MapView] ========== Map click event ==========');
      console.log('[MapView] Click details:', {
        hasPlaceId: !!e.placeId,
        placeId: e.placeId,
        hasLatLng: !!e.latLng,
        lat: e.latLng?.lat(),
        lng: e.latLng?.lng(),
        disabled,
      });

      if (disabled || !e.latLng) {
        console.log('[MapView] Click ignored - disabled or no latLng');
        return;
      }

      if (e.placeId) {
        console.log('[MapView] ✅ POI clicked - placeId:', e.placeId);
        // POI clicked - prevent native InfoWindow and handle it
        if (typeof e.stop === 'function') {
          e.stop();
          console.log('[MapView] Prevented native InfoWindow');
        }
        // Extract coordinates from click event for fallback
        const lat = e.latLng?.lat();
        const lng = e.latLng?.lng();
        console.log('[MapView] Calling onPOIClick handler with coordinates:', { lat, lng });
        onPOIClick(e.placeId, lat, lng)
          .then(() => {
            console.log('[MapView] ✅ onPOIClick completed successfully');
          })
          .catch((error) => {
            console.error('[MapView] ❌ onPOIClick failed:', error);
          });
        return;
      }

      // Regular map click - just update marker position
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      console.log('[MapView] Regular map click (no POI) - updating marker to:', { lat, lng });
      if (markerRef.current) {
        markerRef.current.setPosition({ lat, lng });
        console.log('[MapView] ✅ Marker position updated');
      } else {
        console.warn('[MapView] ⚠️ Marker ref is null, cannot update position');
      }
    });

    // Handle map idle - fires after user finishes dragging/zooming
    const handleIdle = () => {
      // Skip if this was a programmatic move
      if (isProgrammaticMoveRef.current) {
        isProgrammaticMoveRef.current = false;
        return;
      }

      if (disabled) {
        return;
      }

      const center = map.getCenter();
      if (!center || !onIdle) {
        return;
      }

      // Update marker position immediately
      if (markerRef.current) {
        markerRef.current.setPosition(center);
      }

      // Debounce the onIdle callback to avoid excessive calls
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = window.setTimeout(() => {
        const finalCenter = map.getCenter();
        if (finalCenter) {
          onIdle({
            lat: finalCenter.lat(),
            lng: finalCenter.lng(),
          });
        }
      }, 350); // 350ms debounce for reverse geocoding
    };

    // Listen to idle event (fires after drag/zoom completes)
    map.addListener('idle', handleIdle);

    // Handle center change - update marker position in real-time during drag
    map.addListener('center_changed', () => {
      if (disabled || isProgrammaticMoveRef.current) {
        return;
      }

      const center = map.getCenter();
      if (center !== null && markerRef.current) {
        // Update marker position immediately as map center changes (smooth drag)
        markerRef.current.setPosition(center);
      }
    });

    // Handle zoom change - update marker position immediately (visual only)
    // Also prevent zoom changes during programmatic moves
    const zoomChangeListener = map.addListener('zoom_changed', () => {
      // If this is a programmatic move, restore the saved zoom
      if (isProgrammaticMoveRef.current) {
        // Get the zoom that was saved before the programmatic move
        // This will be handled by the selectedPlace effect's idle listener
        return;
      }

      if (disabled) {
        return;
      }

      const center = map.getCenter();
      if (center !== null && markerRef.current) {
        // Update marker position immediately when zooming (visual feedback)
        markerRef.current.setPosition(center);
      }
    });

    // Cleanup
    return () => {
      window.clearTimeout(idleTimerRef.current);
      window.clearTimeout(retryTimerRef.current);
      if (observer) {
        observer.disconnect();
      }
      const googleMaps = window.google?.maps;
      if (googleMaps?.event && zoomChangeListener) {
        googleMaps.event.removeListener(zoomChangeListener);
      }
      // DO NOT nullify mapInstanceRef or markerRef - they should persist across re-renders
      // Only cleanup listeners and timers
    };
  }, [mapRef, mapOptions, onPOIClick, onIdle, disabled, mapsReady]); // Include mapsReady to trigger re-run when Google Maps loads

  // Update map center when selectedPlace changes
  useEffect(() => {
    console.log('[MapView] ========== selectedPlace changed ==========');
    console.log('[MapView] selectedPlace:', {
      hasPlace: !!selectedPlace,
      place_id: selectedPlace?.place_id,
      name: selectedPlace?.name,
      lat: selectedPlace?.lat,
      lng: selectedPlace?.lng,
    });

    if (!mapInstanceRef.current || !selectedPlace) {
      console.log('[MapView] Skipping update - no map instance or selectedPlace');
      return;
    }

    // Set programmatic move flag BEFORE moving map
    isProgrammaticMoveRef.current = true;

    // Pan to selected place (DO NOT touch zoom - let user's zoom level persist)
    // Simple approach: just panTo() and let it naturally preserve zoom
    if (selectedPlace.lat && selectedPlace.lng) {
      const position = { lat: selectedPlace.lat, lng: selectedPlace.lng };
      console.log('[MapView] Panning map to selected place:', position);
      
      // Use panTo which preserves zoom - no restoration logic needed
      mapInstanceRef.current.panTo(position);
    }

    // Update marker
    if (markerRef.current) {
      console.log('[MapView] Updating marker position to selected place');
      markerRef.current.setPosition({ lat: selectedPlace.lat, lng: selectedPlace.lng });
    } else {
      console.warn('[MapView] ⚠️ Marker ref is null, cannot update position');
    }

    // Flag will be cleared in handleIdle when map becomes idle
    // No need for setTimeout - idle event handles it
  }, [selectedPlace]);

  // Force resize when component mounts/updates
  useEffect(() => {
    if (mapInstanceRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (window.google?.maps && mapInstanceRef.current) {
            window.google.maps.event.trigger(mapInstanceRef.current, 'resize');
            const c = mapInstanceRef.current.getCenter();
            if (c) {
              mapInstanceRef.current.setCenter(c);
            }
          }
        });
      });
    }
  }, []);

  return (
    <>
      <style>{`
        .gmp-internal-camera-control,
        .gmp-internal-camera-control.gmnoprint,
        .gmp-internal-camera-control.gmnoprint > button,
        .gmp-internal-camera-control.gmnoprint > button:nth-child(1),
        .gmp-internal-camera-control.gmnoprint > button:nth-child(2) {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
        }
      `}</style>
      <div ref={mapRef} className="w-full h-full" />
      {/* Locate Me Button */}
      <button
        onClick={onLocateMe}
        className="absolute bottom-40 right-4 rounded-full shadow-lg bg-white w-11 h-11 flex items-center justify-center z-20 hover:bg-gray-50"
        title={t('locationPicker.useMyLocation') || 'Use my location'}
        aria-label={t('locationPicker.useMyLocation') || 'Use my location'}
      >
        <MapPin className="w-5 h-5 text-gray-700" />
      </button>
    </>
  );
};

